import { PrivacyLink, TermsLink } from '@/components/modals';
import { getNestedBoolean, getNestedString, useConfig } from '@/utils/useConfig';
import { Typography } from '@mui/material';

interface TermsPrivacyFooterProps {
    /**
     * Optional custom class name for styling
     */
    className?: string;
    /**
     * Optional custom short name to use instead of loading from config
     */
    shortName?: string;
}

/**
 * TermsPrivacyFooter Component
 * 
 * A reusable footer component that conditionally displays terms of service and privacy policy
 * links based on configuration. Handles all combinations: both enabled, only terms, only privacy,
 * or neither enabled.
 * 
 * @param className - Optional CSS class name for styling
 * @param shortName - Optional short name override (defaults to config value)
 * @returns JSX element containing the footer with appropriate links
 */
function TermsPrivacyFooter({ className = '', shortName }: TermsPrivacyFooterProps) {
    const { config } = useConfig('auth_pages_config'); // Use auth_pages_config to get shortName if not provided
    const { config: termsConfig } = useConfig('terms_config');
    const { config: privacyConfig } = useConfig('privacy_config');

    const termsEnabled = getNestedBoolean(termsConfig, 'enable') as boolean;
    const privacyEnabled = getNestedBoolean(privacyConfig, 'enable') as boolean;

    // Use provided shortName or fall back to config
    const appShortName = shortName || getNestedString(config, 'shortName');

    /**
     * Renders the appropriate combination of terms and privacy links
     * @returns JSX element with the correct links and punctuation
     */
    const renderLinks = () => {
        if (termsEnabled && privacyEnabled) {
            return (
                <>
                    <TermsLink /> and <PrivacyLink />.
                </>
            );
        } else if (termsEnabled) {
            return (
                <>
                    <TermsLink />.
                </>
            );
        } else if (privacyEnabled) {
            return (
                <>
                    <PrivacyLink />.
                </>
            );
        }

        return null;
    };

    // Don't render anything if neither terms nor privacy are enabled
    if (!termsEnabled && !privacyEnabled) {

        return null;
    }

    return (
        <div className={`flex items-baseline font-medium gap-2 mt-32 ${className}`}>
            <Typography>
                By continuing to {appShortName}, you agree to our {renderLinks()}
            </Typography>
        </div>
    );
}

export default TermsPrivacyFooter;